
import React, { useState } from 'react';
import { AuthGuard } from '@/components/AuthGuard';
import StatCard from '@/modules/app/components/dashboard/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart3, FileText, TrendingUp, ChevronUp, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Import our new usage management components
import { 
  UsageOverviewCard, 
  UsageHistoryTable, 
  CreditPurchaseModal,
  useUsageTracking,
  UsageToasts
} from '@/modules/app/components/usage';


const AppDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  
  // Usage tracking hook
  const { 
    usageSummary, 
    transactions, 
    loading: usageLoading, 
    forceRefresh 
  } = useUsageTracking(userId || '');
  
  // Stats data - now dynamic based on actual usage
  const stats = [
    { 
      title: "Searches This Month", 
      value: usageSummary ? (usageSummary.total_search_credits_used || 0).toString() : "0", 
      icon: BarChart3, 
      description: "" 
    },
    { 
      title: "Keywords Saved", 
      value: "75", // Keep static for now - could be dynamic later
      icon: FileText, 
      description: "" 
    },
    { 
      title: "AI Analysis Generated", 
      value: usageSummary ? (usageSummary.total_ai_analysis_credits_used || 0).toString() : "0", 
      icon: TrendingUp, 
      description: "" 
    },
  ];

  const handlePurchaseComplete = (packageId: string, amount: number) => {
    toast.success(UsageToasts.creditsPurchased(amount, 'mixed'));
    forceRefresh(); // Refresh usage data
  };

  return (
    <AuthGuard>
      <div className="container mx-auto p-4 md:p-8 space-y-8">
        {/* Welcome Section with Primary CTA */}
        <div className="space-y-6">
          <div className="text-center space-y-4">
            <h1 className="text-3xl font-bold tracking-tight">Welcome to Keyword OS!</h1>
            <p className="text-muted-foreground text-lg">
              Your keyword research and content strategy dashboard
            </p>
          </div>
        </div>

        {/* Your Stats Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold tracking-tight">Your Stats!</h2>
            <Button variant="ghost" size="icon">
              <ChevronUp className="h-5 w-5" />
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {stats.map((stat) => (
              <StatCard 
                key={stat.title} 
                title={stat.title} 
                value={stat.value} 
                icon={stat.icon}
                description={stat.description} // Will render nothing if description is empty
              />
            ))}
          </div>
        </div>

        {/* Usage Management Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Usage Overview Card */}
          <div className="lg:col-span-1">
            {usageSummary && (
              <UsageOverviewCard 
                usage={usageSummary}
                onPurchaseClick={() => setShowPurchaseModal(true)}
              />
            )}
          </div>
          
          {/* Usage Tabs */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="overview" className="space-y-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="history">Usage History</TabsTrigger>
                <TabsTrigger value="purchase">Purchase Credits</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Usage Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {usageSummary ? (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <h4 className="font-medium">Free Usage Today</h4>
                          <div className="text-2xl font-bold text-green-600">
                            {usageSummary.daily_searches_remaining}/5
                          </div>
                          <p className="text-sm text-muted-foreground">Searches remaining</p>
                        </div>
                        
                        <div className="space-y-2">
                          <h4 className="font-medium">Free Usage This Week</h4>
                          <div className="text-2xl font-bold text-purple-600">
                            {usageSummary.weekly_ai_analyses_remaining}/1
                          </div>
                          <p className="text-sm text-muted-foreground">AI analysis remaining</p>
                        </div>
                        
                        {(usageSummary.search_credits > 0 || usageSummary.ai_analysis_credits > 0) && (
                          <>
                            <div className="space-y-2">
                              <h4 className="font-medium">Search Credits</h4>
                              <div className="text-2xl font-bold text-blue-600">
                                {usageSummary.search_credits}
                              </div>
                              <p className="text-sm text-muted-foreground">Available</p>
                            </div>
                            
                            <div className="space-y-2">
                              <h4 className="font-medium">AI Analysis Credits</h4>
                              <div className="text-2xl font-bold text-purple-600">
                                {usageSummary.ai_analysis_credits}
                              </div>
                              <p className="text-sm text-muted-foreground">Available</p>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        {usageLoading ? "Loading usage data..." : "No usage data available"}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="history" className="space-y-4">
                <UsageHistoryTable 
                  transactions={transactions}
                  loading={usageLoading}
                  onExport={() => {
                    // TODO: Implement CSV export
                    toast.info("Export functionality coming soon!");
                  }}
                />
              </TabsContent>
              
              <TabsContent value="purchase" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Purchase Credits</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">
                        Purchase credits to continue using KeywordOS beyond your free limits.
                      </p>
                      <Button 
                        onClick={() => setShowPurchaseModal(true)}
                        size="lg"
                      >
                        View Credit Packages
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Quick Actions</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Button 
              variant="outline" 
              className="h-auto p-4 flex flex-col items-center space-y-2"
              onClick={() => navigate('/app/workspace')}
            >
              <FileText className="h-6 w-6" />
              <span>View All Workspaces</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto p-4 flex flex-col items-center space-y-2"
              onClick={() => navigate('/app/keyword-research')}
            >
              <Search className="h-6 w-6" />
              <span>New Research</span>
            </Button>
          </div>
        </div>

        {/* Credit Purchase Modal */}
        <CreditPurchaseModal
          open={showPurchaseModal}
          onOpenChange={setShowPurchaseModal}
          onPurchaseComplete={handlePurchaseComplete}
        />
      </div>
    </AuthGuard>
  );
};

export default AppDashboardPage;
